import { TooltipProps } from 'recharts';

import { ChartConfig } from 'components/ui/chart';

import {
  DEFAULT_CHART_AXIS_TICK_STYLE,
  SMALL_CHART_AXIS_TICK_STYLE
} from '../analyticsChart.config';
import { NO_DATA_PLACEHOLDER } from '../utils';

import {
  IAnalyticsStackedTimeSeriesPoint,
  IAnalyticsStackedTimeSeriesSegment
} from './AnalyticsStackedTimeSeriesBox.types';

const THOUSAND = 1000;
const ONE_DECIMAL_PLACE = 1;

const ROTATED_X_AXIS_HEIGHT_PX = 60;
const DEFAULT_X_AXIS_HEIGHT_PX = 30;

export interface IXAxisLayout {
  tickStyle: typeof DEFAULT_CHART_AXIS_TICK_STYLE;
  heightPx: number;
  textAnchor: 'end' | 'middle';
}

export const getXAxisLayout = (xAxisTickAngle?: number): IXAxisLayout => {
  const isTickRotated = xAxisTickAngle !== undefined && xAxisTickAngle !== 0;
  if (isTickRotated) {
    return {
      tickStyle: SMALL_CHART_AXIS_TICK_STYLE,
      heightPx: ROTATED_X_AXIS_HEIGHT_PX,
      textAnchor: 'end'
    };
  }
  return {
    tickStyle: DEFAULT_CHART_AXIS_TICK_STYLE,
    heightPx: DEFAULT_X_AXIS_HEIGHT_PX,
    textAnchor: 'middle'
  };
};

export const CHART_HEIGHT_PX = 235;

export const EMPTY_CHART_CONFIG: ChartConfig = {};

type BarRadius = [number, number, number, number] | number;

const TOP_OF_STACK_BAR_RADIUS: BarRadius = [4, 4, 0, 0];
const FLAT_BAR_RADIUS: BarRadius = 0;

export const formatMilliseconds = (value: number): string => {
  return `${value.toLocaleString()} ms`;
};

const formatThousands = (thousands: number): string => {
  if (Number.isInteger(thousands)) {
    return `${thousands}K`;
  }
  return `${thousands.toFixed(ONE_DECIMAL_PLACE)}K`;
};

export const formatCompactNumber = (value: number): string => {
  if (Math.abs(value) < THOUSAND) {
    return value.toString();
  }
  return formatThousands(value / THOUSAND);
};

export const formatSegmentSummary = (
  segment: IAnalyticsStackedTimeSeriesSegment,
  valueFormatter?: (value: number) => string
): string => {
  if (segment.value === undefined || !valueFormatter) {
    return `${segment.label} : ${NO_DATA_PLACEHOLDER}`;
  }
  return `${segment.label} : ${valueFormatter(segment.value)}`;
};

export type ChartDataPoint = {
  date: string;
  totalValue?: number;
  [key: string]: string | number | undefined;
};

export const mapTimeSeriesToChartData = (
  timeSeries: IAnalyticsStackedTimeSeriesPoint[]
): ChartDataPoint[] => {
  return timeSeries.map((point) => ({
    ...point.segmentValues,
    date: point.date,
    totalValue: point.totalValue
  }));
};

export const buildSegmentGradientId = (
  idPrefix: string,
  segmentKey: string
): string => `${idPrefix}-gradient-${segmentKey}`;

export const resolveBarFill = (
  segment: IAnalyticsStackedTimeSeriesSegment,
  idPrefix: string
): string => {
  if (segment.gradient) {
    return `url(#${buildSegmentGradientId(idPrefix, segment.key)})`;
  }
  return segment.color;
};

export const resolveBarRadius = (
  segmentIndex: number,
  segmentsCount: number
): BarRadius => {
  const isTopOfStack = segmentIndex === segmentsCount - 1;
  if (isTopOfStack) {
    return TOP_OF_STACK_BAR_RADIUS;
  }
  return FLAT_BAR_RADIUS;
};

export const getPrimaryMetricDisplayValue = (
  primaryMetricValue: number | undefined,
  valueFormatter: (value: number) => string
): string => {
  if (primaryMetricValue === undefined) {
    return NO_DATA_PLACEHOLDER;
  }
  return valueFormatter(primaryMetricValue);
};

export const getTooltipTitle = (
  label: string,
  tooltipTitleFormatter?: (date: string) => string
): string => {
  if (tooltipTitleFormatter) {
    return tooltipTitleFormatter(label);
  }
  return label;
};

export const formatPayloadValue = (
  value: number | string | undefined,
  valueFormatter: (value: number) => string
): string => {
  if (typeof value === 'number') {
    return valueFormatter(value);
  }
  return String(value ?? '');
};

export const resolveTotalValue = (
  payload: TooltipProps<number, string>['payload']
): number | undefined => {
  const firstPayloadDataPoint = payload?.[0]?.payload;
  if (
    firstPayloadDataPoint &&
    typeof firstPayloadDataPoint.totalValue === 'number'
  ) {
    return firstPayloadDataPoint.totalValue;
  }
  return undefined;
};

export const findSegmentByKey = (
  segments: IAnalyticsStackedTimeSeriesSegment[],
  segmentKey: string | number | undefined
): IAnalyticsStackedTimeSeriesSegment | undefined => {
  return segments.find(
    (candidateSegment) => candidateSegment.key === segmentKey
  );
};
